// Pixel-art icons for the two currencies. The PNGs ship 8×8 from
// `public/currency/`; we render them through the shared
// `image-rendering: pixelated` rule so any integer scale-up stays
// crunchy. One component for both kinds so AppHeader / PlayerStatus /
// the challenge / shop reward chips can drop a consistent icon
// without each callsite re-rolling the styling.

const SRC = {
  juice: "/currency/droplet.png",
  gold: "/currency/coin.png",
} as const;

const ALT = {
  juice: "Void Juice",
  gold: "Gold",
} as const;

export function CurrencyIcon({
  kind,
  size = 16,
  tint,
}: {
  kind: "juice" | "gold";
  /** Edge length in px. Defaults to 16 (native ×2) so the icon stays
   *  crisp without sub-pixel scaling. Always pass an integer multiple
   *  of 8 — the source PNGs are 8×8 native. */
  size?: number;
  /** Optional tint colour. When provided, the icon switches to a
   *  mask-based render (alpha-driven cutout filled with `tint`) so the
   *  reward chip's icon can colour-match the chip's text. When unset,
   *  the natural PNG is shown — same as the wallet chips. */
  tint?: string;
}) {
  if (tint) {
    // `image-rendering: pixelated` is honored on mask images in
    // current Chromium and Firefox. Combined with explicit `<size>px`
    // mask sizing (instead of `contain`, which can subpixel-shift the
    // mask), the icon stays crisp under integer upscales — required
    // because the source PNGs are 8×8 native.
    return (
      <span
        aria-label={ALT[kind]}
        role="img"
        style={{
          display: "inline-block",
          width: size,
          height: size,
          backgroundColor: tint,
          WebkitMaskImage: `url(${SRC[kind]})`,
          maskImage: `url(${SRC[kind]})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
          WebkitMaskSize: `${size}px ${size}px`,
          maskSize: `${size}px ${size}px`,
          imageRendering: "pixelated",
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <img
      src={SRC[kind]}
      alt={ALT[kind]}
      width={size}
      height={size}
      style={{
        display: "inline-block",
        imageRendering: "pixelated",
        flexShrink: 0,
      }}
    />
  );
}
